import React, { useState } from "react";
import { ExternalLink, PlayCircle, Mic, X, Award } from "lucide-react";

const media = [
  {
    type: "TEDx",
    title: "Navigating Toxic Organizations: Healing Through Forgiveness",
    venue: "TEDx OshKosh · January 2023",
    year: "2023",
    icon: PlayCircle,
    embedType: "youtube",
    youtubeId: "_nauTfn8G7U",
    startSeconds: 51,
    url: "https://www.youtube.com/watch?v=_nauTfn8G7U&t=51s",
  },
  {
    type: "Podcast",
    title: "Chaos Is a Gift: Transforming Conflict into Growth Through Leadership",
    venue: "Marketer of the Day",
    year: "2025",
    icon: Mic,
    embedType: "youtube",
    youtubeId: "CYBUzeMtA5Y",
    startSeconds: 5,
    url: "https://www.youtube.com/watch?v=CYBUzeMtA5Y&t=5s",
  },
  {
    type: "Podcast",
    title: "Leadership Challenges, Forgiveness, Dark Leadership",
    venue: "The Leadership Trust",
    year: "2023",
    icon: Mic,
    url: "https://open.spotify.com/episode/3WXfqPkZAU5WnlWkY5MN6A",
  },
  {
    type: "Podcast",
    title: "Developing a Leadership Philosophy | Dr. Andrew Campbell",
    venue: "Keep the Line Moving with Chris Gargano",
    year: "2024",
    icon: Mic,
    embedType: "youtube",
    youtubeId: "vYeth5rMeCs",
    url: "https://www.youtube.com/watch?v=vYeth5rMeCs",
  },
  {
    type: "Keynote",
    title: "Character-Based Leadership in Times of Crisis",
    venue: "Rocheston Reinvent Conference",
    year: "2021",
    icon: PlayCircle,
    embedType: "youtube",
    youtubeId: "4f60iNuOqT0",
    url: "https://www.youtube.com/watch?v=4f60iNuOqT0",
  },
];

const VideoLightbox = ({ item, onClose }) => {
  if (!item) return null;
  const src =
    item.embedType === "youtube"
      ? `https://www.youtube.com/embed/${item.youtubeId}?autoplay=1${
          item.startSeconds ? `&start=${item.startSeconds}` : ""
        }&rel=0`
      : null;
  return (
    <div
      data-testid="media-lightbox"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-10"
      onClick={onClose}
    >
      <button
        data-testid="media-lightbox-close"
        aria-label="Close"
        onClick={onClose}
        className="absolute top-6 right-6 text-white/80 hover:text-white"
      >
        <X className="w-7 h-7" />
      </button>
      <div
        className="w-full max-w-5xl aspect-video bg-black rounded-md overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {src && (
          <iframe
            src={src}
            title={item.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center px-6">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#D9C5C9] mb-1">
          {item.type} · {item.year}
        </div>
        <div className="text-white font-serif text-lg">{item.title}</div>
        <div className="text-white/70 text-sm">{item.venue}</div>
      </div>
    </div>
  );
};

const Media = () => {
  const [active, setActive] = useState(null);

  const handleClick = (m, e) => {
    if (m.embedType === "youtube") {
      e.preventDefault();
      setActive(m);
    }
  };

  return (
    <div data-testid="media-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Media</div>
      <h1 className="text-4xl sm:text-5xl font-serif font-semibold text-[#2D2628] mb-4">
        Talks, press, and publications
      </h1>
      <p className="text-base text-[#6B5E62] max-w-2xl mb-12 leading-relaxed">
        A selection of Dr. Campbell's TEDx appearances, keynote talks, and podcast conversations.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {media.map((m, i) => (
          <a
            key={i}
            href={m.url}
            onClick={(e) => handleClick(m, e)}
            target={m.embedType ? undefined : "_blank"}
            rel={m.embedType ? undefined : "noopener noreferrer"}
            data-testid={`media-item-${i}`}
            className="group bg-white border border-[#E8DCDD] rounded-md p-6 flex gap-5 hover:border-[#8C4A57] transition-colors duration-200 cursor-pointer"
          >
            <div className="flex-shrink-0 w-12 h-12 bg-[#FAF4F5] rounded-sm flex items-center justify-center">
              <m.icon className="w-5 h-5 text-[#8C4A57]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2">
                {m.type} · {m.year}
              </div>
              <h3 className="font-serif text-xl text-[#2D2628] group-hover:text-[#8C4A57] transition-colors duration-200 leading-snug">
                {m.title}
              </h3>
              <div className="text-sm text-[#6B5E62] mt-1">{m.venue}</div>
            </div>
            {m.embedType === "youtube" ? (
              <PlayCircle className="w-5 h-5 text-[#6B5E62] group-hover:text-[#8C4A57] shrink-0" />
            ) : (
              <ExternalLink className="w-4 h-4 text-[#6B5E62] group-hover:text-[#8C4A57] shrink-0" />
            )}
          </a>
        ))}
      </div>

      {/* Press */}
      <section data-testid="press-release-section" className="mt-20">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">Press Release</div>
        <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628] mb-8 leading-tight">
          Featured in the press
        </h2>
        <div className="bg-white border border-[#E8DCDD] rounded-md p-8 sm:p-10 flex flex-col md:flex-row gap-6">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 bg-[#FAF4F5] rounded-sm flex items-center justify-center">
              <Award className="w-7 h-7 text-[#8C4A57]" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-2xl text-[#2D2628] mb-1">The Inner Circle</h3>
            <div className="text-sm text-[#8C4A57] uppercase tracking-widest mb-4">
              Leaders in Healthcare · Business · Law
            </div>
            <p className="text-[#6B5E62] leading-relaxed">
              <span className="text-[#2D2628] font-medium">Omaha, NE</span> — Prominently featured in
              The Inner Circle, Dr. Andrew H. Campbell is acknowledged as a Pinnacle Professional
              Member for his contributions to Organizational Conflict Management, Emotional
              Intelligence, and Problem-Solving and Decision Making for public and private corporate
              executives.
            </p>
            <a
              href="https://incirclexec.com/andrew-h-campbell-director/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="press-release-read-more"
              className="btn-outline mt-6 inline-flex text-xs"
            >
              Read more <ExternalLink className="w-3 h-3 ml-2" />
            </a>
          </div>
        </div>
      </section>

      <VideoLightbox item={active} onClose={() => setActive(null)} />
    </div>
  );
};

export default Media;
